// The ink runner (decision 0054: dialogue in ink, run by inkjs): one inkjs Story per compiled script, its external
// functions bound to the game's (usually `storyBindings`, bindings.ts: ink reads and writes story state through the
// shared conditions). Lines come out as string ids from their `#id:` tags; a line or choice without one throws (the
// compile step's check catches it first; this is the backstop). The seed of ink's shuffles is fixed, so a script runs
// the same in every run. Faults: `INK_FAULTS.dropLineId` strips the id from the next line read (planted defect).
import { Story } from "inkjs";
import type { FactValue } from "@rpg/story";
import { MissingStringIdError, tagValue, type DialogueBeat, type DialogueRunner } from "./runner";

/** The external functions every script may call (declared by the compile step's header, compile.ts). */
export interface InkBindings {
  /** A fact's value (`fact("affinity.child")`). */
  fact(name: string): FactValue;
  /** A condition's truth (`cond("item.copper-ore >= 2")`). */
  cond(expr: string): boolean;
  /** Set or add to a story variable. */
  set(name: string, value: FactValue): void;
  add(name: string, amount: number): void;
  /** Move a quest ("offered", "active", "done", "failed"). */
  quest(id: string, state: string): void;
  /** Change an NPC's affinity, a faction's standing. */
  affinity(npc: string, amount: number): void;
  standing(faction: string, amount: number): void;
  /** Give the item to the NPC being talked to; returns the affinity it made. */
  gift(item: string): number;
  /** The NPC being talked to remembers `key`. */
  remember(key: string, value: FactValue): void;
  /** A game action (a scene, a shop): `act("scene", "meet-elder")`. */
  act(name: string, arg: FactValue): void;
  /** Give the player items, take them back (returns whether they had them). */
  give(item: string, count: number): void;
  take(item: string, count: number): boolean;
}

/** The binding names, in the header's order. */
export const INK_EXTERNALS: readonly { name: keyof InkBindings; args: readonly string[]; pure: boolean }[] = [
  { name: "fact", args: ["name"], pure: true },
  { name: "cond", args: ["expr"], pure: true },
  { name: "set", args: ["name", "value"], pure: false },
  { name: "add", args: ["name", "amount"], pure: false },
  { name: "quest", args: ["id", "state"], pure: false },
  { name: "affinity", args: ["npc", "amount"], pure: false },
  { name: "standing", args: ["faction", "amount"], pure: false },
  { name: "gift", args: ["item"], pure: false },
  { name: "remember", args: ["key", "value"], pure: false },
  { name: "act", args: ["name", "arg"], pure: false },
  { name: "give", args: ["item", "count"], pure: false },
  { name: "take", args: ["item", "count"], pure: false },
];

/** The ink runner's planted defects. */
export const INK_FAULTS = {
  /** The next line read loses its `#id:` tag (the runner must throw). */
  dropLineId: false,
};

interface InkSnapshot {
  states: Record<string, string>;
  active: string | null;
}

/** An ink runner over compiled scripts (`{ name: compiled JSON }`). `seed`: ink's shuffle seed. */
export function createInkRunner(o: { scripts: Readonly<Record<string, string | object>>; bindings: InkBindings; seed?: number }): DialogueRunner {
  const stories = new Map<string, Story>();
  const story = (name: string): Story => {
    let s = stories.get(name);
    if (s) return s;
    const src = o.scripts[name];
    if (src === undefined) throw new Error(`dialogue: no ink script "${name}" (have ${Object.keys(o.scripts).join(", ")})`);
    s = new Story(typeof src === "string" ? src : JSON.stringify(src));
    s.state.storySeed = o.seed ?? 1;
    s.onError = (msg) => {
      throw new Error(`ink "${name}": ${msg}`);
    };
    const b = o.bindings;
    const fns: Record<keyof InkBindings, (...a: never[]) => unknown> = {
      fact: (n: string) => b.fact(n),
      cond: (e: string) => b.cond(e),
      set: (n: string, v: FactValue) => b.set(n, v),
      add: (n: string, a: number) => b.add(n, a),
      quest: (id: string, st: string) => b.quest(id, st),
      affinity: (npc: string, a: number) => b.affinity(npc, a),
      standing: (f: string, a: number) => b.standing(f, a),
      gift: (item: string) => b.gift(item),
      remember: (k: string, v: FactValue) => b.remember(k, v),
      act: (n: string, a: FactValue) => b.act(n, a),
      give: (item: string, c: number) => b.give(item, c),
      take: (item: string, c: number) => b.take(item, c),
    };
    for (const ext of INK_EXTERNALS) {
      const fn = fns[ext.name] as (...a: unknown[]) => unknown;
      // ink wants a value back from every call; a void binding returns null (ink's "nothing")
      s.BindExternalFunction(ext.name, (...a: unknown[]) => {
        const r = fn(...a);
        return r === undefined ? null : r;
      }, ext.pure);
    }
    stories.set(name, s);
    return s;
  };
  let active: string | null = null;
  const need = (): Story => {
    if (!active) throw new Error("dialogue: no script started");
    return story(active);
  };
  return {
    scripts: () => Object.keys(o.scripts),
    start(script, knot) {
      const s = story(script);
      active = script;
      if (knot) s.ChoosePathString(knot);
    },
    next(): DialogueBeat {
      const s = need();
      while (s.canContinue) {
        const text = s.Continue() ?? "";
        let tags = [...(s.currentTags ?? [])];
        if (INK_FAULTS.dropLineId && tags.some((t) => t.trim().startsWith("id:"))) {
          INK_FAULTS.dropLineId = false;
          tags = tags.filter((t) => !t.trim().startsWith("id:"));
        }
        const id = tagValue(tags, "id");
        // logic-only output (a blank line between calls) carries no words: skip it
        if (!id && !/\p{L}/u.test(text)) continue;
        if (!id) throw new MissingStringIdError(active!, text);
        return { kind: "line", id, speaker: tagValue(tags, "speaker"), tags };
      }
      if (s.currentChoices.length) {
        return {
          kind: "choices",
          choices: s.currentChoices.map((c) => {
            const tags = [...(c.tags ?? [])];
            const id = tagValue(tags, "id");
            if (!id) throw new MissingStringIdError(active!, c.text);
            return { index: c.index, id, tags };
          }),
        };
      }
      return { kind: "end" };
    },
    choose(index) {
      need().ChooseChoiceIndex(index);
    },
    snapshot(): InkSnapshot {
      const states: Record<string, string> = {};
      for (const [k, s] of stories) states[k] = s.state.ToJson();
      return { states, active };
    },
    restore(data) {
      const d = data as InkSnapshot;
      if (!d || typeof d.states !== "object") throw new Error("dialogue: not an ink runner snapshot");
      for (const [k, s] of stories) if (!(k in d.states)) s.ResetState(), (s.state.storySeed = o.seed ?? 1);
      for (const [k, json] of Object.entries(d.states)) story(k).state.LoadJson(json);
      active = d.active;
    },
  };
}
